import type { AnchorHTMLAttributes, MouseEvent, ReactElement } from 'react'
import { useState } from 'react'

import { type ListItemProps } from '@material-ui/core/ListItem'

import { ListItemIconStyled } from './styles/ListItemIconStyled'
import { ListItemTextStyled } from './styles/ListItemTextStyled'
import { MenuListItemStyled } from './styles/MenuListItemStyled'
import { type IconTypes, Icon } from '@ui/elements/Icon'
import { type IWithTooltipProps, WithTooltip } from '@ui/elements/Tooltip'
import { getAnalyticsDataAttrs } from '@ui/utils/analytics'

export interface IMenuListItemProps extends ListItemProps {
  icon?: IconTypes | ReactElement
  text: string | ReactElement
  sticky?: boolean
  disabled?: boolean
  selected?: boolean
  selectedIcon?: IconTypes
  closeOnSelect?: boolean
  centerText?: boolean
  tooltip?: IWithTooltipProps['title']
  linkProps?: AnchorHTMLAttributes<HTMLAnchorElement>
  onClick?(e: MouseEvent<HTMLElement>): void
  /** Unique identifier for analytics tracking for menu list item interactions. */
  analyticsId?: string
}

export const MenuListItem = ({
  text,
  icon,
  selectedIcon,
  sticky = false,
  disabled = false,
  selected = false,
  closeOnSelect = null,
  centerText = false,
  tooltip,
  linkProps,
  onClick = () => undefined,
  analyticsId,
  ...props
}: IMenuListItemProps) => {
  const [isSelected, setIsSelected] = useState(selected)
  const analyticsProps = getAnalyticsDataAttrs(analyticsId)

  const handleMenuItemClick = (e: MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      onClick(e)
    }
    setIsSelected(!isSelected)
  }

  const renderMenuItem = () => (
    <MenuListItemStyled
      className='menu-list-item'
      disabled={disabled}
      selected={isSelected}
      onClick={handleMenuItemClick}
      aria-label={props['aria-label']}
      {...analyticsProps}
    >
      {typeof icon === 'string' && (
        <ListItemIconStyled className={icon}>
          <Icon
            icon={isSelected && selectedIcon ? selectedIcon : icon}
            ariaLabel={isSelected ? 'selected icon' : 'icon'}
            color='darkGrey'
            size={16}
          />
        </ListItemIconStyled>
      )}

      {typeof icon !== 'string' && (
        <ListItemIconStyled>{icon}</ListItemIconStyled>
      )}

      <ListItemTextStyled centerText={centerText} inset={true} primary={text} />
    </MenuListItemStyled>
  )

  const renderMenuListItem = () => {
    return linkProps ? (
      <a {...linkProps}>{renderMenuItem()}</a>
    ) : (
      renderMenuItem()
    )
  }

  return tooltip ? (
    <WithTooltip title={tooltip} placement='left'>
      <div>{renderMenuListItem()}</div>
    </WithTooltip>
  ) : (
    renderMenuListItem()
  )
}
